import React, { useEffect, useRef, useState } from 'react';

const INTRO_PARAGRAPHS = [
  'Flarix.ai ("Flarix", "we", "us", or "our") provides AI-powered floor planning, architectural visualization, design assistance, and related digital services through its website, applications, software, and other digital platforms (collectively, the "Platform").',
  'By accessing or using Flarix.ai, you acknowledge and agree to the following disclaimer.',
];

const DISCLAIMER_SECTIONS = [
  {
    number: '01',
    title: 'AI-Generated Design Output',
    blocks: [
      { p: 'The floor plans, layouts, room arrangements, measurements, recommendations, visualizations, and other design outputs generated through Flarix.ai may be created or assisted by artificial intelligence and automated software.' },
      { p: <>Such outputs are intended to provide <strong>preliminary design concepts, planning assistance, inspiration, and visualization</strong>.</> },
      { p: 'AI-generated outputs may contain errors, omissions, inconsistencies, or inaccuracies, including but not limited to:' },
      { list: ['Dimensions or measurements', 'Room sizes', 'Wall positions', 'Door and window locations', 'Staircase dimensions or positioning', 'Structural elements', 'Electrical points', 'Plumbing locations', 'Furniture placement', 'Building setbacks', 'Site utilization', 'Orientation', 'Ventilation', 'Lighting', 'Structural feasibility', 'Local building regulations', 'Zoning requirements', 'Fire-safety requirements', 'Accessibility requirements', 'Other architectural or engineering considerations.'] },
      { warn: 'Flarix.ai outputs should not be considered final architectural, structural, engineering, construction, legal, or regulatory documents.' },
    ],
  },
  {
    number: '02',
    title: 'Not Construction-Ready Drawings',
    blocks: [
      { p: <>Unless expressly stated otherwise in writing by Flarix.ai, any floor plan or design generated through the Platform is <strong>not a construction-ready drawing</strong> and should not be used directly for construction, structural execution, approval, estimation, or statutory submission.</> },
      { p: 'Before beginning construction or renovation, users should have the relevant plans reviewed, modified, and approved by appropriately qualified and licensed professionals, including architects, structural engineers, civil engineers, MEP consultants, or other professionals as applicable.' },
      { p: 'The user is solely responsible for obtaining all required approvals, permissions, sanctions, licenses, and clearances from relevant authorities.' },
    ],
  },
  {
    number: '03',
    title: 'Site Conditions',
    blocks: [
      { p: 'Flarix.ai may generate designs based on information provided by the user.' },
      { p: 'Flarix does not independently verify the accuracy of information submitted by the user, including:' },
      { list: ['Plot dimensions', 'Site boundaries', 'Road width', 'North direction', 'Setbacks', 'Existing structures', 'Ground levels', 'Soil conditions', 'Local development regulations', 'Utility locations', 'Other site-specific information.'] },
      { p: 'Any incorrect or incomplete information may result in an unsuitable or inaccurate output.' },
      { p: 'Users should independently verify all site information before relying on any design.' },
    ],
  },
  {
    number: '04',
    title: 'Local Laws and Building Regulations',
    blocks: [
      { p: 'Building regulations differ by location and may change from time to time.' },
      { p: 'A design generated by Flarix.ai may not comply with the applicable:' },
      { list: ['Building bye-laws', 'Development control regulations', 'Zoning regulations', 'Municipal regulations', 'Fire regulations', 'Environmental requirements', 'Structural codes', 'Accessibility requirements', 'Floor-area restrictions', 'Setback requirements', 'Other applicable laws and regulations.'] },
      { p: 'The user is responsible for obtaining professional advice regarding compliance with applicable laws and regulations.' },
    ],
  },
  {
    number: '05',
    title: 'No Guarantee of Approval',
    blocks: [
      { p: 'Flarix does not guarantee that any design generated through the Platform will:' },
      { list: ['Receive approval from a municipal authority', 'Receive approval from any government department', 'Comply with local regulations', 'Be accepted by an architect, engineer, contractor, lender, insurer, society, or authority', "Be suitable for construction at the user's site."] },
    ],
  },
  {
    number: '06',
    title: 'Vastu and Other Design Principles',
    blocks: [
      { p: <>Where the Platform provides Vastu-related suggestions or incorporates Vastu principles, such information is provided for <strong>informational and design-assistance purposes only</strong>.</> },
      { p: 'Flarix does not guarantee any particular outcome, benefit, financial result, health outcome, personal outcome, or other result from following Vastu or other traditional design principles.' },
      { p: 'Users should independently determine whether they wish to rely on such principles.' },
    ],
  },
  {
    number: '07',
    title: 'Estimates and Cost Information',
    blocks: [
      { p: 'Any construction cost estimates, material quantities, area calculations, pricing information, or other financial estimates generated or displayed by Flarix.ai are indicative only.' },
      { p: 'Actual costs may vary significantly based on:' },
      { list: ['Location', 'Labour rates', 'Material prices', 'Site conditions', 'Contractor rates', 'Design modifications', 'Market conditions', 'Taxes', 'Transportation', 'Construction methods', 'Other factors.'] },
      { p: 'Such information should not be treated as a quotation, guarantee, or binding commercial offer.' },
    ],
  },
  {
    number: '08',
    title: 'Third-Party Professionals',
    blocks: [
      { p: 'Where Flarix introduces, refers, displays, or facilitates access to architects, engineers, contractors, consultants, vendors, suppliers, or other third parties, such parties may operate independently.' },
      { p: 'Unless expressly stated otherwise, Flarix does not guarantee:' },
      { list: ['Their qualifications', 'Availability', 'Pricing', 'Performance', 'Work quality', 'Timelines', 'Compliance', 'Professional advice', 'Results.'] },
      { p: 'Any agreement entered into directly with a third party may be subject to separate terms between the user and that third party.' },
    ],
  },
  {
    number: '09',
    title: 'Technology and Availability',
    blocks: [
      { p: 'Flarix.ai relies on software, artificial intelligence models, cloud infrastructure, APIs, databases, telecommunications networks, and other technology.' },
      { p: 'Flarix does not guarantee that the Platform will always be:' },
      { list: ['Available', 'Uninterrupted', 'Error-free', 'Secure from every possible threat', 'Compatible with every device', 'Free from technical failures.'] },
      { p: 'Temporary interruptions, maintenance, software errors, third-party service failures, or other technical issues may occur.' },
    ],
  },
  {
    number: '10',
    title: 'User Responsibility',
    blocks: [
      { p: 'Users are responsible for reviewing all information and outputs generated through Flarix.ai before relying upon them.' },
      { warn: 'By using the Platform, you acknowledge that you remain responsible for decisions made using any information or design generated through the Platform.' },
    ],
  },
  {
    number: '11',
    title: 'Professional Advice',
    blocks: [
      { p: 'Information available through Flarix.ai should not be considered a substitute for professional architectural, structural, engineering, legal, financial, regulatory, or construction advice.' },
      { p: 'Where professional advice is required, users should consult an appropriately qualified professional.' },
    ],
  },
  {
    number: '12',
    title: 'Limitation of Reliance',
    blocks: [
      { p: 'Flarix.ai is a design-assistance and planning tool. The availability of an output through the Platform does not constitute a representation that the output is technically, structurally, legally, financially, or commercially suitable for a particular project.' },
    ],
  },
  {
    number: '13',
    title: 'Changes to This Disclaimer',
    blocks: [
      { p: 'Flarix may update or modify this Disclaimer from time to time. The updated version will become effective when published on the Platform.' },
    ],
  },
  {
    number: '14',
    title: 'Contact',
    blocks: [
      { p: 'For questions regarding this Disclaimer, please contact:' },
      {
        contact: [
          { label: 'Flarix.ai', value: '' },
          { label: 'Email', value: 'support@flarix.ai' },
          { label: 'Website', value: 'https://flarix.ai' },
        ]
      },
    ],
  },
];

const CLOSING_NOTICE = 'BY USING FLARIX.AI, YOU ACKNOWLEDGE THAT AI-GENERATED DESIGN OUTPUTS ARE PRELIMINARY DIGITAL OUTPUTS AND MUST BE PROFESSIONALLY REVIEWED BEFORE CONSTRUCTION OR STATUTORY USE.';

function renderBlock(block, key) {
  if (block.p) return <p className="dp-text" key={key}>{block.p}</p>;
  if (block.h) return <h3 className="dp-subhead" key={key}>{block.h}</h3>;
  if (block.warn) {
    return (
      <div className="dp-warn" key={key}>
        <span className="dp-warn-badge">!</span>
        <p>{block.warn}</p>
      </div>
    );
  }
  if (block.list) {
    return (
      <ul className="dp-list" key={key}>
        {block.list.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.contact) {
    return (
      <div className="dp-contact" key={key}>
        {block.contact.map((row, i) =>
          row.value ? (
            <div className="dp-contact-row" key={i}>
              <span className="dp-contact-label">{row.label}</span>
              <span className="dp-contact-value">{row.value}</span>
            </div>
          ) : (
            <div className="dp-contact-title" key={i}>{row.label}</div>
          )
        )}
      </div>
    );
  }
  return null;
}

function Disclaimer() {
  const [activeId, setActiveId] = useState(DISCLAIMER_SECTIONS[0].number);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.dataset.number);
          }
        });
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="disclaimer-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

        .disclaimer-page {
          --ink: #0b1d2e;
          --violet: #8b5cf6;
          --cyan: #5ec8e8;
          --cyan-deep: #0e7490;
          --warn: #b45309;
          --warn-bg: #fffaf0;
          --warn-border: #fbd38d;
          --line: rgba(94, 200, 232, 0.14);
          --line-strong: rgba(94, 200, 232, 0.4);
          --text: #2b2740;
          --text-muted: #6b7280;
          --border: #ece9f7;
          --panel: #f7f6fd;
          font-family: 'Poppins', sans-serif;
          background: #fff;
          color: var(--text);
          line-height: 1.75;
          width: 100%;
          overflow-x: hidden;
        }
        .disclaimer-page * {
          box-sizing: border-box;
        }

        .disclaimer-page .dp-hero {
          position: relative;
          overflow: hidden;
          background: var(--ink);
          background-image:
            repeating-linear-gradient(0deg, var(--line) 0 1px, transparent 1px 42px),
            repeating-linear-gradient(90deg, var(--line) 0 1px, transparent 1px 42px);
          padding: 5rem 1.5rem 6.5rem;
          text-align: center;
          border-bottom: 1px solid var(--line-strong);
        }

        .disclaimer-page .dp-corner {
          position: absolute;
          width: 18px;
          height: 18px;
          z-index: 1;
        }
        .disclaimer-page .dp-corner::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: var(--line-strong);
        }
        .disclaimer-page .dp-corner::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 1px;
          background: var(--line-strong);
        }
        .disclaimer-page .dp-corner--tl { top: 20px; left: 20px; }
        .disclaimer-page .dp-corner--tr { top: 20px; right: 20px; }
        .disclaimer-page .dp-corner--bl { bottom: 20px; left: 20px; }
        .disclaimer-page .dp-corner--br { bottom: 20px; right: 20px; }

        .disclaimer-page .dp-logo-wrap {
          position: relative;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.9rem 1.4rem;
          background: rgba(94, 200, 232, 0.05);
          border: 1px solid var(--line-strong);
          border-radius: 8px;
          margin-bottom: 1.75rem;
        }
        .disclaimer-page .dp-logo-wrap img {
          height: 40px;
          width: auto;
        }

        .disclaimer-page .dp-eyebrow {
          position: relative;
          z-index: 2;
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--cyan);
          margin: 0 0 0.6rem;
        }

        .disclaimer-page .dp-hero h1 {
          position: relative;
          z-index: 2;
          font-size: clamp(2.2rem, 5vw, 3.1rem);
          font-weight: 800;
          letter-spacing: -0.01em;
          color: #fff;
          text-shadow: 0 2px 30px rgba(94, 200, 232, 0.25);
          margin: 0 0 0.9rem;
        }

        .disclaimer-page .dp-hero-sub {
          position: relative;
          z-index: 2;
          max-width: 520px;
          margin: 0 auto 1.5rem;
          color: rgba(220, 238, 246, 0.75);
          font-size: 1rem;
          font-weight: 400;
        }

        .disclaimer-page .dp-updated-pill {
          position: relative;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(94, 200, 232, 0.08);
          border: 1px solid var(--line-strong);
          color: var(--cyan);
          font-size: 0.82rem;
          font-weight: 500;
          padding: 0.4rem 0.9rem;
          border-radius: 999px;
        }

        .disclaimer-page .dp-body {
          max-width: 1120px;
          margin: -3rem auto 0;
          padding: 0 1.5rem 5rem;
          position: relative;
          z-index: 3;
        }

        .disclaimer-page .dp-lead {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 20px;
          box-shadow: 0 20px 45px -25px rgba(76, 29, 149, 0.35);
          padding: 2.25rem 2.5rem;
          margin-bottom: 2.5rem;
        }
        .disclaimer-page .dp-lead p {
          margin: 0 0 0.9rem;
          color: var(--text-muted);
          font-size: 0.98rem;
        }
        .disclaimer-page .dp-lead p:last-child {
          margin-bottom: 0;
        }

        .disclaimer-page .dp-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          min-width: 0;
        }
        @media (min-width: 1024px) {
          .disclaimer-page .dp-layout {
            grid-template-columns: 250px 1fr;
            align-items: start;
          }
        }

        .disclaimer-page .dp-index {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 18px;
          padding: 1.25rem;
          min-width: 0;
        }
        @media (min-width: 1024px) {
          .disclaimer-page .dp-index {
            position: sticky;
            top: 1.5rem;
            max-height: calc(100vh - 3rem);
            overflow-y: auto;
          }
        }

        .disclaimer-page .dp-index-label {
          display: block;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--text-muted);
          margin: 0 0 0.85rem;
          padding: 0 0.4rem;
        }

        .disclaimer-page .dp-index ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        @media (max-width: 1023px) {
          .disclaimer-page .dp-index ul {
            flex-direction: row;
            overflow-x: auto;
            gap: 0.5rem;
            padding-bottom: 0.25rem;
          }
          .disclaimer-page .dp-index a {
            white-space: nowrap;
          }
        }

        .disclaimer-page .dp-index a {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          font-size: 0.82rem;
          font-weight: 500;
          color: var(--text-muted);
          text-decoration: none;
          padding: 0.5rem 0.6rem;
          border-radius: 10px;
        }
        .disclaimer-page .dp-index a:hover {
          background: #fff;
          color: var(--cyan-deep);
        }
        .disclaimer-page .dp-index a.is-active {
          background: #fff;
          color: var(--cyan-deep);
          font-weight: 600;
          box-shadow: 0 4px 12px -6px rgba(14, 116, 144, 0.5);
        }
        .disclaimer-page .dp-index a span {
          font-size: 0.68rem;
          font-weight: 700;
          color: var(--violet);
        }

        .disclaimer-page .dp-sections {
          display: flex;
          flex-direction: column;
          gap: 1.35rem;
          min-width: 0;
        }

        .disclaimer-page .dp-section {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 18px;
          padding: 1.85rem 2rem;
          scroll-margin-top: 1.5rem;
        }

        .disclaimer-page .dp-section-head {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.1rem;
        }
        .disclaimer-page .dp-section-number {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--violet), var(--cyan-deep));
          color: #fff;
          font-weight: 700;
          font-size: 0.95rem;
        }
        .disclaimer-page .dp-section-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--ink);
          margin: 0;
        }

        .disclaimer-page .dp-subhead {
          font-size: 0.92rem;
          font-weight: 600;
          color: var(--cyan-deep);
          margin: 1.1rem 0 0.5rem;
        }

        .disclaimer-page .dp-text {
          margin: 0 0 0.75rem;
          color: var(--text);
          font-size: 0.96rem;
        }
        .disclaimer-page .dp-text:last-child {
          margin-bottom: 0;
        }

        .disclaimer-page .dp-list {
          margin: 0 0 0.75rem;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
        }
        .disclaimer-page .dp-list:last-child {
          margin-bottom: 0;
        }
        .disclaimer-page .dp-list li {
          position: relative;
          padding-left: 1.15rem;
          font-size: 0.94rem;
          color: var(--text);
        }
        .disclaimer-page .dp-list li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.55em;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--violet);
        }

        .disclaimer-page .dp-warn {
          display: flex;
          align-items: flex-start;
          gap: 0.7rem;
          background: var(--warn-bg);
          border: 1px solid var(--warn-border);
          border-left: 3px solid var(--warn);
          border-radius: 10px;
          padding: 0.9rem 1.1rem;
          margin: 0.25rem 0 0.75rem;
        }
        .disclaimer-page .dp-warn:last-child {
          margin-bottom: 0;
        }
        .disclaimer-page .dp-warn-badge {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--warn);
          color: #fff;
          font-size: 0.75rem;
          font-weight: 700;
          margin-top: 0.1rem;
        }
        .disclaimer-page .dp-warn p {
          margin: 0;
          color: #7c2d12;
          font-weight: 600;
          font-size: 0.92rem;
        }

        .disclaimer-page .dp-contact {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 1.1rem 1.3rem;
          margin: 0.5rem 0 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }
        .disclaimer-page .dp-contact-title {
          font-weight: 700;
          color: var(--ink);
          font-size: 0.95rem;
        }
        .disclaimer-page .dp-contact-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          font-size: 0.9rem;
        }
        .disclaimer-page .dp-contact-label {
          font-weight: 600;
          color: var(--ink);
        }
        .disclaimer-page .dp-contact-value {
          color: var(--text-muted);
          font-style: italic;
        }

        .disclaimer-page .dp-closing {
          display: flex;
          align-items: flex-start;
          gap: 0.9rem;
          background: linear-gradient(135deg, rgba(180, 83, 9, 0.06), rgba(180, 83, 9, 0.02));
          border: 1px solid var(--warn-border);
          border-radius: 18px;
          padding: 1.75rem 2rem;
          margin-top: 2rem;
        }
        .disclaimer-page .dp-closing-badge {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 10px;
          background: var(--warn);
          color: #fff;
          font-weight: 700;
          font-size: 1rem;
        }
        .disclaimer-page .dp-closing p {
          margin: 0;
          color: #7c2d12;
          font-weight: 700;
          font-size: 0.9rem;
          letter-spacing: 0.01em;
          line-height: 1.6;
        }

        .disclaimer-page .dp-footer {
          text-align: center;
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 1px solid var(--border);
          color: var(--text-muted);
          font-size: 0.85rem;
        }

        @media (max-width: 640px) {
          .disclaimer-page .dp-hero {
            padding: 4rem 1.25rem 6rem;
          }
          .disclaimer-page .dp-lead,
          .disclaimer-page .dp-section,
          .disclaimer-page .dp-closing {
            padding: 1.5rem 1.4rem;
          }
        }
      `}</style>

      <div className="dp-hero">
        <span className="dp-corner dp-corner--tl" aria-hidden="true" />
        <span className="dp-corner dp-corner--tr" aria-hidden="true" />
        <span className="dp-corner dp-corner--bl" aria-hidden="true" />
        <span className="dp-corner dp-corner--br" aria-hidden="true" />
        <div className="dp-logo-wrap">
          <img loading="lazy" src="/images/logo.png" className="img-fluid" alt="Flarix.ai" />
        </div>
        <h1>Disclaimer</h1>
        <p className="dp-hero-sub">
          What to know before relying on Flarix.ai's AI-generated floor plans and design outputs.
        </p>
        {/* <span className="dp-updated-pill">Last updated: [Date]</span> */}
      </div>

      <div className="dp-body">
        <div className="dp-lead">
          {INTRO_PARAGRAPHS.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </div>

        <div className="dp-layout">
          <nav className="dp-index" aria-label="Disclaimer sections">
            <span className="dp-index-label">On this page</span>
            <ul>
              {DISCLAIMER_SECTIONS.map((s) => (
                <li key={s.number}>
                  <a
                    href={`#section-${s.number}`}
                    className={activeId === s.number ? 'is-active' : ''}
                  >
                    <span>{s.number}</span>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="dp-sections">
            {DISCLAIMER_SECTIONS.map((section) => (
              <section
                id={`section-${section.number}`}
                className="dp-section"
                key={section.number}
                data-number={section.number}
                ref={(el) => (sectionRefs.current[section.number] = el)}
              >
                <div className="dp-section-head">
                  <span className="dp-section-number">{section.number}</span>
                  <h2 className="dp-section-title">{section.title}</h2>
                </div>
                {section.blocks.map((block, i) => renderBlock(block, i))}
              </section>
            ))}
          </div>
        </div>

        <div className="dp-closing">
          <span className="dp-closing-badge">!</span>
          <p>{CLOSING_NOTICE}</p>
        </div>

        <div className="dp-footer">© Flarix.ai — All rights reserved.</div>
      </div>
    </div>
  );
}

export default Disclaimer;